// src/pages/EmployeesPage.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Users, Key } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { userApi } from '../api';
import EmployeeDialog from '../components/admin/EmployeeDialog';
import ResetPasswordDialog from '../components/admin/ResetPasswordDialog';

const EmployeesPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];

  const isHQ = currentUser?.store_type === 'headquarters';

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const res = await userApi.getList();
      if (res.success) setEmployees(res.data || []);
    } catch (err) {
      console.error('加载员工失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingEmployee(null);
    setDialogOpen(true);
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setDialogOpen(true);
  };

  const handleResetPassword = (employee) => {
    setSelectedEmployee(employee);
    setResetPasswordOpen(true);
  };

  const handleSubmit = async (data) => {
    try {
      if (editingEmployee) {
        const res = await userApi.update(editingEmployee.id, data);
        if (res.success) alert(language === 'en' ? 'Updated successfully' : '更新成功');
      } else {
        const res = await userApi.create(data);
        if (res.success) alert(language === 'en' ? 'Created successfully' : '创建成功');
      }
      setDialogOpen(false);
      loadEmployees();
    } catch (err) {
      console.error('提交失败:', err);
      alert(language === 'en' ? 'Operation failed' : '操作失败');
    }
  };

  const handlePasswordReset = async (data) => {
    try {
      const res = await userApi.resetPassword(selectedEmployee.id, data);
      if (res.success) {
        alert(language === 'en' ? 'Password reset successfully' : '密码重置成功');
        setResetPasswordOpen(false);
        setSelectedEmployee(null);
      }
    } catch (err) {
      console.error('重置密码失败:', err);
      alert(language === 'en' ? 'Password reset failed' : '密码重置失败');
    }
  };

  const getRoleBadge = (role) => {
    const map = {
      admin:   { label: { zh: '管理员', en: 'Admin', km: 'អ្នកគ្រប់គ្រង' }, color: 'bg-purple-100 text-purple-800' },
      manager: { label: { zh: '店长',   en: 'Manager', km: 'អ្នកគ្រប់គ្រងហាង' }, color: 'bg-orange-100 text-orange-800' },
      cashier: { label: { zh: '收银员', en: 'Cashier', km: 'អ្នកគិតលុយ' }, color: 'bg-blue-100 text-blue-800' }
    };
    const r = map[role] || { label: { zh: role, en: role, km: role }, color: 'bg-gray-100 text-gray-800' };
    const lang = language === 'en' ? 'en' : language === 'km' ? 'km' : 'zh';
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${r.color}`}>
        {r.label[lang]}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">{language === 'en' ? 'Active' : language === 'km' ? 'សកម្ម' : '活跃'}</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{language === 'en' ? 'Inactive' : language === 'km' ? 'អសកម្ម' : '停用'}</span>;
  };

 return (
  <div className="p-6 h-full overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
  <div>
    <h2 className="text-xl font-bold">
      {language === 'en' ? 'Employee Management' : language === 'km' ? 'គ្រប់គ្រងបុគ្គលិក' : '员工管理'}
    </h2>
    <p className="text-xs text-gray-600 mt-1">
      {language === 'en' ? 'Manage system users and permissions' : language === 'km' ? 'គ្រប់គ្រងអ្នកប្រើប្រាស់និងសិទ្ធ' : '管理系统用户和权限'}
    </p>
  </div>
  <button
    onClick={handleAdd}
    className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
  >
    <Plus className="w-4 h-4" />
    <span>{language === 'en' ? 'Add Employee' : language === 'km' ? 'បន្ថែមបុគ្គលិក' : '添加员工'}</span>
  </button>
</div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">{language === 'en' ? 'Loading...' : '加载中...'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {language === 'en' ? 'Username' : '用户名'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {language === 'en' ? 'Name' : '姓名'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {language === 'en' ? 'Role' : '角色'}
                </th>
                {/* 总店管理员额外显示门店列 */}
                {isHQ && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {language === 'en' ? 'Store' : '所属门店'}
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {language === 'en' ? 'Status' : '状态'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {language === 'en' ? 'Actions' : '操作'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.map((employee) => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{employee.id}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{employee.username}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{employee.name || '-'}</td>
                  <td className="px-6 py-4">{getRoleBadge(employee.role)}</td>
                  {isHQ && (
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {employee.store_name || '-'}
                    </td>
                  )}
                  <td className="px-6 py-4">{getStatusBadge(employee.status)}</td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(employee)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title={t.edit}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(employee)}
                        className="text-orange-600 hover:text-orange-800 transition-colors"
                        title={language === 'en' ? 'Reset Password' : '重置密码'}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {employees.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>{language === 'en' ? 'No employees found' : '暂无员工'}</p>
            </div>
          )}
        </div>
      )}

      {dialogOpen && (
        <EmployeeDialog
          employee={editingEmployee}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {resetPasswordOpen && selectedEmployee && (
        <ResetPasswordDialog
          employee={selectedEmployee}
          onClose={() => { setResetPasswordOpen(false); setSelectedEmployee(null); }}
          onSubmit={handlePasswordReset}
        />
      )}
    </div>
  );
};

export default EmployeesPage;